/**
 * Workout aggregates for the Set tab + Swim tab metric rings.
 *
 * Stage 7 ships the minimum needed for the rings: week-distance, streak,
 * peak-HR-from-derived. Stage 6 extends with monthly rollups, multi-swim
 * SWOLF averages, and the week-strip day occupancy array.
 *
 * Week-start is Monday per ISO 8601 (Scott's pick during planning).
 */
import type { WorkoutSummary } from "@contract/types";
import type { DerivedWorkout } from "./lapAnalysis";

/** Returns the Monday 00:00 of the ISO week containing `d`. */
export function startOfIsoWeek(d: Date): Date {
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const delta = day === 0 ? -6 : 1 - day;
  const out = new Date(d);
  out.setDate(d.getDate() + delta);
  out.setHours(0, 0, 0, 0);
  return out;
}

/** Total meters across swims whose startDate falls in the ISO week of `anchor`. */
export function weekDistanceMeters(
  swims: WorkoutSummary[],
  anchor: Date = new Date(),
): number {
  const start = startOfIsoWeek(anchor);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  let total = 0;
  for (const s of swims) {
    const ts = new Date(s.startDate);
    if (ts < start || ts >= end) continue;
    total += s.totalDistance?.value ?? 0;
  }
  return total;
}

/** Per-week aggregate. weekStartISO anchors the week (Monday 00:00 of the ISO
 *  week containing the swims). swimsByDay is length 7, Monday-first. */
export interface WeekAggregate {
  weekStartISO: string;
  totalDistanceMeters: number;
  totalDurationSec: number;
  swimCount: number;
  /** length 7, Mon..Sun. true if at least one swim that day. */
  swimsByDay: boolean[];
}

/** Build a WeekAggregate for the ISO week containing `anchor`. */
export function aggregateWeek(
  swims: WorkoutSummary[],
  anchor: Date = new Date(),
): WeekAggregate {
  const start = startOfIsoWeek(anchor);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);

  const swimsByDay = new Array<boolean>(7).fill(false);
  let totalDistanceMeters = 0;
  let totalDurationSec = 0;
  let swimCount = 0;

  for (const s of swims) {
    const ts = new Date(s.startDate);
    if (ts < start || ts >= end) continue;
    swimCount += 1;
    totalDistanceMeters += s.totalDistance?.value ?? 0;
    totalDurationSec += s.durationSeconds;
    const day = ts.getDay(); // Sun=0
    const idx = day === 0 ? 6 : day - 1; // Mon=0..Sun=6
    swimsByDay[idx] = true;
  }

  return {
    weekStartISO: start.toISOString(),
    totalDistanceMeters,
    totalDurationSec,
    swimCount,
    swimsByDay,
  };
}

/** Last N ISO weeks ending with `anchor`'s week (inclusive), oldest first.
 *  Useful for the month bar chart on the Set tab. */
export function aggregateRecentWeeks(
  swims: WorkoutSummary[],
  weekCount: number,
  anchor: Date = new Date(),
): WeekAggregate[] {
  const weeks: WeekAggregate[] = [];
  const cursor = startOfIsoWeek(anchor);
  for (let i = weekCount - 1; i >= 0; i--) {
    const weekAnchor = new Date(cursor);
    weekAnchor.setDate(cursor.getDate() - i * 7);
    weeks.push(aggregateWeek(swims, weekAnchor));
  }
  return weeks;
}

/**
 * Consecutive calendar days, counting backward from `anchor`, where at least
 * one swim was recorded. Today counts if Scott has already swum today; if
 * not, the streak starts from yesterday backward.
 */
export function currentStreak(
  swims: WorkoutSummary[],
  anchor: Date = new Date(),
): number {
  const swimDates = new Set(
    swims.map((s) => new Date(s.startDate).toDateString()),
  );

  // Walk backward from anchor, optionally skipping today if no swim today
  const cursor = new Date(anchor);
  cursor.setHours(0, 0, 0, 0);

  if (!swimDates.has(cursor.toDateString())) {
    // No swim today — start counting from yesterday
    cursor.setDate(cursor.getDate() - 1);
  }

  let streak = 0;
  while (swimDates.has(cursor.toDateString())) {
    streak++;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak;
}

/**
 * Consecutive calendar weeks (ISO, Monday start) with at least one swim,
 * counting backward from the current week. The Scores tab's "Weeks in a row"
 * ring reads this (owner decision 2026-07-07 — a scoped reversal of the
 * cumulative weeksSwumThisYear shape for THAT surface; weeksSwumThisYear
 * stays for anything else that wants the no-streak framing).
 *
 * Warm by design: the CURRENT week never breaks the run — if it has no swim
 * yet (it's Monday, or a rest day), counting simply starts from last week.
 * Only a fully-missed week ends the streak. Mirrors currentStreak's
 * "no swim today → start from yesterday" day logic, one level up.
 */
export function weeksInARow(
  swims: WorkoutSummary[],
  anchor: Date = new Date(),
): number {
  if (swims.length === 0) return 0;
  const weekKeys = new Set(
    swims.map((s) => startOfIsoWeek(new Date(s.startDate)).getTime()),
  );
  const cursor = startOfIsoWeek(anchor);
  if (!weekKeys.has(cursor.getTime())) {
    // No swim in the in-progress week — start counting from last week.
    cursor.setDate(cursor.getDate() - 7);
  }
  let streak = 0;
  while (weekKeys.has(cursor.getTime())) {
    streak++;
    cursor.setDate(cursor.getDate() - 7);
  }
  return streak;
}

/** Peak heart-rate sample value across all HR samples in the derived workout. */
export function peakHrBpm(dw: DerivedWorkout | null): number | null {
  if (!dw) return null;
  const series = dw.hrSeries;
  if (series.length === 0) return null;
  let max = -Infinity;
  for (const point of series) {
    if (point.bpm > max) max = point.bpm;
  }
  return max === -Infinity ? null : Math.round(max);
}

/** Overall SWOLF for the active swim — Stage 7 surfaces this single-swim
 *  value in the rings; Stage 6 will replace with a multi-swim weekly average. */
export function swolfMean(dw: DerivedWorkout | null): number | null {
  if (!dw || !dw.swolfStats) return null;
  return Math.round(dw.swolfStats.overallAvg);
}

/**
 * Cumulative count of distinct ISO weeks with at least one swim in the
 * calendar year of `anchor`, plus the number of ISO weeks elapsed so far
 * in that year (the denominator).
 *
 * Replaces the prior consecutive-week-streak shape. The chain framing
 * manufactured Duolingo-style anxiety on rest weeks; cumulative-this-year
 * never punishes a skip — last year's track record is intact, you just
 * keep adding distinct weeks until December 31. Aligns with Self-
 * Determination Theory (autonomy/competence) over loss-aversion mechanics.
 *
 * Ring fill = count / weeksElapsed. Fully filled = swum every ISO week
 * of the year so far. A skipped week pulls the fill down by ~1/N each
 * week, which is a soft, recoverable signal rather than a reset.
 */
export function weeksSwumThisYear(
  swims: WorkoutSummary[],
  anchor: Date = new Date(),
): { count: number; weeksElapsed: number } {
  const year = anchor.getFullYear();
  const yearStart = new Date(year, 0, 1);
  const yearWeekStart = startOfIsoWeek(yearStart);
  const anchorWeekStart = startOfIsoWeek(anchor);

  const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
  const weeksElapsed = Math.max(
    1,
    Math.floor(
      (anchorWeekStart.getTime() - yearWeekStart.getTime()) / MS_PER_WEEK,
    ) + 1,
  );

  const weekKeys = new Set<string>();
  for (const s of swims) {
    const d = new Date(s.startDate);
    if (d.getFullYear() !== year) continue;
    weekKeys.add(startOfIsoWeek(d).toISOString());
  }

  return { count: weekKeys.size, weeksElapsed };
}
